// "Kırılıma dönüştür" sihirbazı saf mantığı (spec §6.4, §18 DR6): başlangıç formu (hedef önerisi,
// boyut önerisi, üye etiketi önerisi), adım kapıları, `POST …/breakdown/convert` gövdesi ve sunucu
// hatası → dal (409 değerli kategori / 422 birim / 403 kategori kapısı / diğer). Önizleme ve
// uygulama AYNI gövdeyle gider (yalnız `dryRun` farklı) — sayılar birebir aynıdır (sunucu planı).

import { type KpiBreakdownConvertBody, type KpiMetric } from "@mahir/shared";
import { ApiError } from "../api/client";
import { breakdownErrorText, KPI_BREAKDOWN_COPY } from "./kpi-breakdown-copy";
import {
  customDimensionLabelOf,
  dimensionSlug,
  DIMENSION_SLUG_RE,
  initialGroupForm,
  setFormDimension,
  type GroupForm,
} from "./kpi-breakdown-group-form";
import {
  partitionSiblingCandidates,
  type SiblingIneligibleReason,
  categoryMembers,
  convertTargetOptions,
  siblingPool,
  suggestDimension,
  suggestMemberLabel,
  type CatalogView,
  type ConvertPattern,
} from "./kpi-catalog-breakdown";

export type ConvertStep = 1 | 2 | 3 | "done";
export type ConvertPending = "preview" | "commit" | null;

export interface ConvertMemberDraft {
  metricId: string;
  /** katalog etiketi */
  label: string;
  /** üye adı (öneri, düzenlenebilir) */
  memberLabel: string;
  /** kalıp (b) onay kutusu; kalıp (a)'da hep true */
  checked: boolean;
  /** kalıp (b): alt kalem olamaz (satır pasif, sebep yanında) */
  ineligible?: SiblingIneligibleReason | undefined;
  /** kalıp (b) birim sebebi metni için kardeşin birimi */
  unit?: string | null | undefined;
}

export { partitionSiblingCandidates, type SiblingIneligibleReason };

export interface ConvertForm {
  pattern: ConvertPattern;
  /** kalıp (a) hedef toplam; "" = "Bu kategoriyi toplam göstergesine dönüştür" (yeni toplam) */
  targetId: string;
  /** boyut + grup adı (BreakdownGroupModal ile aynı kurallar; üye listesi kullanılmaz) */
  group: GroupForm;
  members: ConvertMemberDraft[];
}

export function initialConvertForm(
  source: KpiMetric,
  pattern: ConvertPattern,
  metrics: readonly KpiMetric[],
  view: CatalogView,
): ConvertForm {
  const base = initialGroupForm({
    total: {
      metricId: source.id,
      code: source.code,
      label: source.label,
      unit: source.defaultUnit,
    },
    group: null,
    usedDimensions: new Set(),
    calcGroupLabel: null,
    totalClientId: source.clientId,
  });
  const dim = suggestDimension(source.label);
  const group = dim ? setFormDimension(base, dim.dimension) : base;
  const pool =
    pattern === "category"
      ? categoryMembers(view, source.id)
      : siblingPool(view, metrics, source, { includeTotals: true });
  // hasGroups: rowKindsOf kırılım grubu olan göstergeyi `total` işaretler (view.kindOf onu okur)
  const bad = new Map(
    pattern === "siblings"
      ? partitionSiblingCandidates(source, pool, {
          hasGroups: (id) => view.kindOf(id) === "total",
        }).ineligible.map((i) => [i.id, i.reason] as const)
      : [],
  );
  const labels = pool.map((m) => m.label);
  const members: ConvertMemberDraft[] = pool.map((m) => {
    const memberLabel = suggestMemberLabel(source.label, m.label, labels);
    const ineligible = bad.get(m.id);
    return {
      metricId: m.id,
      label: m.label,
      memberLabel,
      unit: m.defaultUnit,
      ...(ineligible ? { ineligible } : {}),
      checked: !ineligible && (pattern === "category" || memberLabel !== m.label.trim()),
    };
  });
  const suggested =
    pattern === "category"
      ? (convertTargetOptions(source, metrics, view).find((o) => o.suggested)?.metric.id ?? "")
      : source.id;
  return { pattern, targetId: suggested, group: { ...group, members: [] }, members };
}

/**
 * Önizleme `derivedMembers` (katalog bayrağı ∪ müşteri bazlı etkin türetilmişlik): seçili üyelerden
 * türetilmiş olanlar pasif + "hesaplanan gösterge alt kalem olamaz" olur, işareti kalkar (K-B7).
 * Hiçbir seçili üye etkilenmediyse aynı form döner (referans eşit).
 */
export function markDerivedMembers(form: ConvertForm, derived: readonly string[]): ConvertForm {
  const set = new Set(derived);
  if (!form.members.some((m) => set.has(m.metricId) && m.ineligible === undefined)) return form;
  return {
    ...form,
    members: form.members.map((m) =>
      set.has(m.metricId) && m.ineligible === undefined
        ? { ...m, ineligible: "derived" as const, checked: false }
        : m,
    ),
  };
}

/** Adım 2 kapısı (adım 1'in seçimi her zaman geçerli). */
export function convertFormGate(form: ConvertForm): { ok: boolean; reason: string | null } {
  const M = KPI_BREAKDOWN_COPY.modal;
  const C = KPI_BREAKDOWN_COPY.convert;
  const fail = (reason: string) => ({ ok: false, reason });
  if (form.group.dimension === "") return fail(C.needDimension);
  if (!DIMENSION_SLUG_RE.test(dimensionSlug(form.group))) return fail(M.customDimensionInvalid);
  if (form.group.label.trim() === "") return fail(M.needLabel);
  const chosen = form.members.filter((m) => m.checked);
  if (chosen.length === 0) return fail(C.needSiblings);
  if (chosen.some((m) => m.memberLabel.trim() === "")) return fail(C.needMemberLabel);
  return { ok: true, reason: null };
}

export function buildConvertBody(form: ConvertForm, dryRun: boolean): KpiBreakdownConvertBody {
  const chosen = form.members.filter((m) => m.checked);
  // özel boyut adı → sunucu yeni grupla birlikte boyut sözlüğüne yazar (grup kaydıyla aynı kural)
  const dimensionLabel = customDimensionLabelOf(form.group);
  const memberLabels: Record<string, string> = {};
  for (const m of chosen) {
    const l = m.memberLabel.trim();
    if (l !== "" && l !== m.label.trim()) memberLabels[m.metricId] = l;
  }
  return {
    pattern: form.pattern,
    ...(form.pattern === "category"
      ? form.targetId
        ? { targetMetricId: form.targetId }
        : {}
      : { siblingIds: chosen.map((m) => m.metricId) }),
    dimension: dimensionSlug(form.group),
    ...(dimensionLabel ? { dimensionLabel } : {}),
    groupLabel: form.group.label.trim(),
    ...(Object.keys(memberLabels).length > 0 ? { memberLabels } : {}),
    dryRun,
  };
}

export type ConvertError =
  | { kind: "has_values"; reason: "values" | "notes" | "formulas"; text: string }
  | { kind: "unit"; metricId: string | null; clientId: string | null; text: string }
  | { kind: "forbidden"; text: string }
  | { kind: "other"; text: string };

/** Sunucu hatası → sihirbaz dalı + metin. */
export function convertErrorOf(err: unknown): ConvertError {
  const C = KPI_BREAKDOWN_COPY.convert;
  if (!(err instanceof ApiError)) {
    return {
      kind: "other",
      text: err instanceof Error ? err.message : KPI_BREAKDOWN_COPY.errorFallback,
    };
  }
  const d = (err.details ?? {}) as Record<string, unknown>;
  if (err.code === "breakdown_convert_category_has_values") {
    const reason = d.reason === "notes" || d.reason === "formulas" ? d.reason : "values";
    return { kind: "has_values", reason, text: C.hasValues[reason] };
  }
  if (err.code === "breakdown_unit_mismatch") {
    return {
      kind: "unit",
      metricId: typeof d.metricId === "string" ? d.metricId : null,
      clientId: typeof d.clientId === "string" ? d.clientId : null,
      text: C.unitMismatch(typeof d.unit === "string" ? d.unit : null),
    };
  }
  if (err.status === 403) return { kind: "forbidden", text: C.forbidden };
  return { kind: "other", text: breakdownErrorText(err.code, err.message) };
}
